import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { ArrowLeft, Shield, Lock, Eye, Mail } from 'lucide-react';

const PageContainer = styled.div`
  min-height: 100vh;
  background: #474545;
  padding: 80px 20px 60px;
`;

const Container = styled.div`
  max-width: 900px;
  margin: 0 auto;
`;

const BackLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: #0097B2;
  text-decoration: none;
  font-weight: 600;
  margin-bottom: 40px;
  transition: all 0.3s ease;

  &:hover {
    transform: translateX(-5px);
    color: #FAECEC;
  }
`;

const Header = styled(motion.div)`
  text-align: center;
  margin-bottom: 60px;
`;

const Title = styled.h1`
  font-size: 3rem;
  font-weight: 700;
  margin-bottom: 16px;
  background: linear-gradient(135deg, #FAECEC 0%, #0097B2 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;

  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const LastUpdated = styled.p`
  color: #FAECEC;
  opacity: 0.7;
  font-size: 0.95rem;
`;

const Content = styled(motion.div)`
  background: rgba(0, 151, 178, 0.05);
  border: 1px solid rgba(0, 151, 178, 0.2);
  border-radius: 20px;
  padding: 50px;
  backdrop-filter: blur(10px);

  @media (max-width: 768px) {
    padding: 30px 20px;
  }
`;

const Section = styled.section`
  margin-bottom: 40px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const SectionTitle = styled.h2`
  font-size: 1.8rem;
  font-weight: 600;
  color: #0097B2;
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  gap: 12px;

  @media (max-width: 768px) {
    font-size: 1.4rem;
  }
`;

const Paragraph = styled.p`
  color: #FAECEC;
  opacity: 0.9;
  line-height: 1.8;
  margin-bottom: 16px;
  font-size: 1rem;
`;

const List = styled.ul`
  color: #FAECEC;
  opacity: 0.9;
  line-height: 1.8;
  margin-left: 24px;
  margin-bottom: 16px;
`;

const ListItem = styled.li`
  margin-bottom: 12px;
`;

const Highlight = styled.span`
  color: #0097B2;
  font-weight: 600;
`;

const SecurityCard = styled.div`
  background: linear-gradient(135deg, rgba(0, 151, 178, 0.1) 0%, rgba(250, 236, 236, 0.05) 100%);
  border: 1px solid rgba(0, 151, 178, 0.3);
  border-radius: 12px;
  padding: 24px;
  margin: 24px 0;
`;

const ContactCard = styled.div`
  background: linear-gradient(135deg, rgba(0, 151, 178, 0.1) 0%, rgba(250, 236, 236, 0.05) 100%);
  border: 1px solid rgba(0, 151, 178, 0.3);
  border-radius: 12px;
  padding: 24px;
  margin-top: 24px;
  display: flex;
  align-items: center;
  gap: 16px;
`;

const ContactIcon = styled.div`
  width: 50px;
  height: 50px;
  background: linear-gradient(135deg, #0097B2 0%, #007a94 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const ContactInfo = styled.div`
  flex: 1;
`;

const ContactTitle = styled.h4`
  color: #FAECEC;
  font-size: 1.1rem;
  font-weight: 600;
  margin-bottom: 4px;
`;

const ContactEmail = styled.a`
  color: #0097B2;
  text-decoration: none;
  font-size: 1rem;
  
  &:hover {
    text-decoration: underline;
  }
`;

const PrivacyPolicy = () => {
  return (
    <PageContainer>
      <Helmet>
        <title>f01i.ai Privacy Policy - Data Security &amp; User Rights Overview</title>
        <meta name="description" content="f01i.ai Privacy Policy: discover what data we collect, how we protect API keys with encryption and read-only access, and how we use and share information." />
        <link rel="canonical" href="https://f01i.ai/privacy-policy" />
      </Helmet>
      <Container>
        <BackLink to="/">
          <ArrowLeft size={20} />
          Back to Home
        </BackLink>

        <Header
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <Title>Privacy Policy</Title>
          <LastUpdated>Last updated: January 2026</LastUpdated>
        </Header>

        <Content
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
        >
          <Section>
            <Paragraph>
              At <Highlight>f01i.ai</Highlight> and <Highlight>f01i.app</Highlight>, your privacy and security are our top priorities. 
              This Privacy Policy explains how we collect, use, protect, and handle your information when you use our AI-powered 
              trading platform and services.
            </Paragraph>
          </Section>

          <Section>
            <SectionTitle>
              <Shield size={24} />
              Information We Collect
            </SectionTitle>
            <Paragraph>
              We collect information that you provide directly to us when using our platform:
            </Paragraph>
            <List>
              <ListItem><Highlight>Account Information:</Highlight> Email address, username, and authentication credentials</ListItem>
              <ListItem><Highlight>Trading Preferences:</Highlight> Bot configurations, trading strategies, and f01i Blocks interactions</ListItem>
              <ListItem><Highlight>Usage Data:</Highlight> Analytics about how you interact with our platform, including pages visited and features used</ListItem>
              <ListItem><Highlight>Communication Data:</Highlight> Messages you send through our support channels or AI chat builder</ListItem>
            </List>
          </Section>

          <SecurityCard>
            <SectionTitle>
              <Lock size={24} />
              Your API Keys & Financial Security
            </SectionTitle>
            <Paragraph>
              <Highlight>We never store or access your funds.</Highlight> All API keys are encrypted and managed securely — you stay in control.
            </Paragraph>
            <List>
              <ListItem>
                <Highlight>Encrypted API Keys:</Highlight> Military-grade encryption protects your exchange API keys. 
                We use industry-standard encryption protocols to ensure your credentials are never exposed.
              </ListItem>
              <ListItem>
                <Highlight>Read-Only Access:</Highlight> We only use read-only API keys, meaning we can only read your data — 
                we can never withdraw or transfer your funds. Your assets remain completely under your control.
              </ListItem>
              <ListItem>
                <Highlight>Secure Infrastructure:</Highlight> Enterprise-level security with regular audits and monitoring. 
                Our systems are continuously monitored for any security threats or vulnerabilities.
              </ListItem>
            </List>
          </SecurityCard>

          <Section>
            <SectionTitle>
              <Eye size={24} />
              How We Use Your Information
            </SectionTitle>
            <Paragraph>
              We use the information we collect to:
            </Paragraph>
            <List>
              <ListItem>Provide, maintain, and improve our AI trading services</ListItem>
              <ListItem>Execute your trading strategies through connected exchanges</ListItem>
              <ListItem>Generate technical analysis and market insights via our f01i AI Agent</ListItem>
              <ListItem>Communicate with you about service updates, security alerts, and support</ListItem>
              <ListItem>Detect and prevent fraudulent or unauthorized activities</ListItem>
              <ListItem>Comply with legal obligations and enforce our terms of service</ListItem>
            </List>
          </Section>

          <Section>
            <SectionTitle>Data Sharing and Disclosure</SectionTitle>
            <Paragraph>
              We do not sell your personal information. We may share your information only in these limited circumstances:
            </Paragraph>
            <List>
              <ListItem><Highlight>With Your Consent:</Highlight> When you explicitly authorize us to share specific information</ListItem>
              <ListItem><Highlight>Service Providers:</Highlight> With trusted third-party services that help us operate our platform (e.g., cloud hosting, analytics)</ListItem>
              <ListItem><Highlight>Legal Compliance:</Highlight> When required by law, regulation, or legal process</ListItem>
              <ListItem><Highlight>Safety & Security:</Highlight> To protect the rights, property, or safety of f01i.ai, our users, or the public</ListItem>
            </List>
          </Section>

          <Section>
            <SectionTitle>Data Retention</SectionTitle>
            <Paragraph>
              We retain your information only as long as necessary to provide our services and comply with legal obligations. 
              You may request deletion of your account and associated data at any time by contacting our support team.
            </Paragraph>
          </Section>

          <Section>
            <SectionTitle>Your Privacy Rights</SectionTitle>
            <Paragraph>
              Depending on your location, you may have the following rights:
            </Paragraph>
            <List>
              <ListItem>Access and review your personal information</ListItem>
              <ListItem>Request corrections to inaccurate data</ListItem>
              <ListItem>Request deletion of your data</ListItem>
              <ListItem>Object to or restrict certain data processing</ListItem>
              <ListItem>Export your data in a portable format</ListItem>
            </List>
          </Section>

          <Section>
            <SectionTitle>Cookies and Tracking</SectionTitle>
            <Paragraph>
              We use cookies and similar technologies to enhance your experience and analyze usage patterns. 
              You can control cookie preferences through your browser settings. For more details, please see our{' '}
              <Link to="/cookie-policy" style={{ color: '#0097B2', textDecoration: 'underline' }}>Cookie Policy</Link>.
            </Paragraph>
          </Section>

          <Section>
            <SectionTitle>International Data Transfers</SectionTitle>
            <Paragraph>
              Your information may be transferred to and processed in countries other than your own. 
              We ensure appropriate safeguards are in place to protect your data in accordance with this Privacy Policy.
            </Paragraph>
          </Section>

          <Section>
            <SectionTitle>Children's Privacy</SectionTitle>
            <Paragraph>
              Our services are not intended for individuals under the age of 18. We do not knowingly collect personal 
              information from children.
            </Paragraph>
          </Section>

          <Section>
            <SectionTitle>Changes to This Policy</SectionTitle>
            <Paragraph>
              We may update this Privacy Policy from time to time. We will notify you of any material changes by 
              posting the new policy on this page and updating the "Last updated" date.
            </Paragraph>
          </Section>

          <Section>
            <SectionTitle>Contact Us</SectionTitle>
            <Paragraph>
              If you have any questions about this Privacy Policy or our privacy practices, please contact us:
            </Paragraph>
            <ContactCard>
              <ContactIcon>
                <Mail size={24} color="#FAECEC" />
              </ContactIcon>
              <ContactInfo>
                <ContactTitle>Email Us</ContactTitle>
                <ContactEmail href="mailto:feedback@f01i.ai">feedback@f01i.ai</ContactEmail>
              </ContactInfo>
            </ContactCard>
          </Section>
        </Content>
      </Container>
    </PageContainer>
  );
};

export default PrivacyPolicy;
